"use client"
import { getPageNameFromPath } from "@/app/(site)/_utils/getPageNameFromPath"
import { readPageViewMetadataScript } from "@/lib/utils/tracking/pageViewGate"
import { pageViewGate } from "@/lib/utils/tracking/sendGTMEvent"
import { useCookieBot } from "@/src/hooks/useCookieBot"
import { usePathname } from "next/navigation"
import { useEffect } from "react"

// Must stay below the gate's orphan timeout (5s) so events held for this page are still
// released behind its page_view rather than without one.
const CONSENT_FALLBACK_MS = 4000

/**
 * Sends `page_view` on first load and on every in-site navigation, once the cookie
 * consent decision is known (Cookiebot `OnConsentReady`), so GA4 never evaluates it
 * against the pre-decision defaults. Falls back after `CONSENT_FALLBACK_MS` when
 * Cookiebot is blocked, absent (draft mode) or slow; the gate ignores a repeated start
 * for the same pathname, so a late decision cannot double-send.
 *
 * Keyed on the pathname only: query-string changes (filters, sort, pagination) are not
 * a new page view, a path change (including a PDP colour change, which rewrites the URI)
 * is. Reads `window.location` and `document.title` inside the effect so the layout is
 * not forced into dynamic rendering (`useSearchParams` would do that).
 */
export function PageViewTracker() {
    const pathname = usePathname()
    const { consentReady } = useCookieBot()

    useEffect(() => {
        const startPage = () =>
            pageViewGate.startPage(
                pathname,
                {
                    page_location: window.location.href,
                    page_path: pathname,
                    page_title: document.title,
                    page_type: getPageNameFromPath(pathname)
                },
                readPageViewMetadataScript()
            )

        if (consentReady) {
            startPage()
            return
        }

        const fallback = setTimeout(startPage, CONSENT_FALLBACK_MS)
        return () => clearTimeout(fallback)
    }, [pathname, consentReady])

    return null
}
